// @ds/forms/SchemaForm — the orchestrator for Pattern 1 (single-record CRUD).
//
// Takes a `FormViewConfig<T>` and a Refine resource, and renders the full
// create/edit modal: title, sections, fields, save/cancel. Wraps
// `<CrudModalShell>` (which already absorbs the AntD Modal foot-guns) and
// `useCrudModalForm` (which already absorbs the useModalForm boilerplate).
//
// What SchemaForm OWNS:
//   - Modal lifecycle (open/close, save-on-success).
//   - Form instance + Refine create/update mutation.
//   - Section rendering with `visibleWhen` (subscribed via Form.useWatch).
//   - Grid pairings (nested-array field entries → <Grid columns={N}>).
//   - Parent-FK injection: `parentValues` is merged into the submit payload
//     and seeded into the form's initialValues — no hidden Form.Item
//     declarations needed in the view config.
//   - beforeSubmit interceptor (sync).
//
// What SchemaForm does NOT own (Pattern 1 scope):
//   - Cross-field derive rules (Form.useWatch → RPC lookup).
//   - Modes / footer-flip state machines (existing-picker, save-anyway).
//   - Async-validate with warning rendering.
//   - Drive uploads / pre-submit side-effects.
// Those land in F3/F4 as additive layers on top of this orchestrator.

import React, { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { App, Form } from "antd";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import type { BaseRecord } from "@refinedev/core";

import { getSchemaMeta } from "@ds/config/schema";
import { CrudModalShell } from "@ds/forms/CrudModalShell";
import { useCrudModalForm } from "@ds/forms/useCrudModalForm";
import { FormSection } from "@ds/forms/FormSection";
import { parseJsonFieldValues } from "@ds/forms/parseJsonFieldValues";
import { SchemaFormItem } from "@ds/forms/SchemaFormItem";
import { Grid } from "@ds/layout/Grid";

import type {
  FieldEntry,
  FormMode,
  FormViewConfig,
  SectionDef,
} from "@ds/forms/formViewConfig";

// ─── Inputs ──────────────────────────────────────────────────────────────

export interface SchemaFormInput<T> {
  /** The form view config. */
  view: FormViewConfig<T>;
  /** Mode — "create" / "edit" / "duplicate". Drives title + save-label + hydrate. */
  mode: FormMode;
  /** Open state — controlled by the parent. */
  open: boolean;
  /** Record id — required for edit mode. Ignored for create. */
  id?: number | string;
  /**
   * For create-from-parent flows: values that get merged into the submit
   * payload as hidden fields (typically a parent FK like `{ company_id: 42 }`).
   * Also seeded into the form so initialValues can reference them.
   */
  parentValues?: Record<string, unknown>;
  /** Override the testid prefix on auto-emitted field testids.
   *  Default: `${resource}-${mode}-form`. */
  formTestIdPrefix?: string;
}

// ─── Outputs ─────────────────────────────────────────────────────────────

export interface SchemaFormOutput<T> {
  /** Fires when modal wants to close. */
  onClose: () => void;
  /** Fires after a successful create / update with the resulting record. */
  onSaved?: (record: T) => void;
}

export type SchemaFormProps<T> = SchemaFormInput<T> & SchemaFormOutput<T>;

// ─── Component ───────────────────────────────────────────────────────────

export function SchemaForm<T extends BaseRecord = BaseRecord>({
  view,
  mode,
  open,
  id,
  parentValues,
  formTestIdPrefix,
  onClose,
  onSaved,
}: SchemaFormProps<T>) {
  const { t } = useTranslation();
  const isEdit = mode === "edit";
  const isDuplicate = mode === "duplicate";

  // Duplicate is a "create" mutation that pre-fills from the source record.
  // useModalForm handles only "create" and "edit"; duplicate maps to create.
  const refineAction: "create" | "edit" = isEdit ? "edit" : "create";

  // `resource` + `entityName` default to the schema's own meta, so view
  // configs only declare them when they diverge from the schema.
  const schemaMeta = getSchemaMeta(view.schema);
  const resource = view.resource ?? schemaMeta.resource;

  const { formProps, form, handleSubmit, handleClose, saving } =
    useCrudModalForm<T>({
      resource,
      action: refineAction,
      ...(isEdit && id != null ? { id } : {}),
      // Non-`id` PK (SchemaMeta.idColumn) — the supabase data provider
      // addresses the edit hydrate + update by this column instead.
      ...(schemaMeta.idColumn
        ? { meta: { idColumnName: schemaMeta.idColumn } }
        : {}),
      open,
      onClose,
      onSaved,
    });

  const { message } = App.useApp();

  /** Local saving flag for `asyncBeforeSubmit` — runs BEFORE useModalForm
   *  fires the mutation, so the mutation's own `saving` doesn't cover it. */
  const [asyncPending, setAsyncPending] = useState(false);
  const effectiveSaving = saving || asyncPending;

  const entityNameStr = view.entityName?.(t) ?? schemaMeta.labels.singular(t);

  const initialValues = useMemo(() => {
    if (refineAction !== "create") return undefined;
    const seeded = view.initialValues?.({ mode, parentValues }) ?? {};
    return { ...(parentValues ?? {}), ...seeded };
  }, [mode, parentValues, refineAction, view]);

  // Submit pipeline:
  //   1. AntD form validation (handled by Form.Item rules)
  //   2. Sync beforeSubmit (normalize / strip)
  //   3. Async beforeSubmit (Drive upload, IBAN remote-validate, etc.)
  //   4. parentValues merge → useModalForm.onFinish → mutation
  // Aborts on async throw — the modal stays open, error toast surfaces.
  const onFinishWrapped = useMemo(() => {
    const base = (formProps as { onFinish?: (v: unknown) => void }).onFinish;
    return async (values: unknown) => {
      const valuesObj = (values ?? {}) as Record<string, unknown>;
      const withParent = { ...(parentValues ?? {}), ...valuesObj };
      const withJson = parseJsonFieldValues(
        withParent,
        view.schema as Record<string, { edit?: { key?: string; field?: { type?: string } } }>,
      );
      const afterSync = view.beforeSubmit
        ? view.beforeSubmit(withJson, mode)
        : withJson;
      let final = afterSync;
      if (view.asyncBeforeSubmit) {
        setAsyncPending(true);
        try {
          const result = await view.asyncBeforeSubmit(afterSync, mode);
          if (result === null) {
            // Silent abort — form manages its own inline UI.
            setAsyncPending(false);
            return;
          }
          final = result;
        } catch (err) {
          setAsyncPending(false);
          // Throw → toast. Same UX as a backend rejection.
          const msg =
            (err as { message?: string } | undefined)?.message ??
            t("common.messages.error", "Wystąpił błąd");
          message.error(msg);
          return;
        } finally {
          setAsyncPending(false);
        }
      }
      base?.(final);
    };
  }, [formProps, mode, parentValues, view, message, t]);

  // Title + save-label resolution — overrides win, otherwise defaults via
  // CrudModalShell's mode-derived strings.
  const titleOverride = view.titleOverride?.(t, mode);
  const saveLabel = view.saveLabelOverride?.(t, mode, {});

  // Default testid prefix follows the convention used in existing forms
  // (e.g. `product-create-form`). Resource + mode produces a unique stable
  // prefix per (resource, mode) combination.
  const testIdPrefix =
    formTestIdPrefix ?? `${resource}-${mode}-form`;

  return (
    <CrudModalShell
      open={open}
      mode={isDuplicate ? "duplicate" : refineAction}
      entityName={entityNameStr}
      title={titleOverride}
      width={view.width ?? 600}
      saveLabel={saveLabel}
      saving={effectiveSaving}
      onClose={handleClose}
      onSubmit={handleSubmit}
      testId={`${resource}-${mode}-modal`}
      formTestIdPrefix={testIdPrefix}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={onFinishWrapped}
        {...(initialValues ? { initialValues } : {})}
      >
        <SectionsRenderer<T>
          sections={view.sections}
          schema={view.schema}
          form={form as unknown as ReturnType<typeof Form.useForm>[0]}
        />
      </Form>
    </CrudModalShell>
  );
}

// ─── Sections renderer — subscribes to form values for visibleWhen ──────
//
// Each section that declares `visibleWhen` must subscribe to all form
// values to re-evaluate the predicate on change. We use a single
// `Form.useWatch([])` at the top to grab the whole values object, then
// pass it down. This keeps the visibility check synchronous and
// dependency-tracked without each FormSection needing its own watcher.

function SectionsRenderer<T>({
  sections,
  schema,
  form,
}: {
  sections: SectionDef<T>[];
  schema: Record<string, unknown>;
  form: ReturnType<typeof Form.useForm>[0];
}) {
  const values = (Form.useWatch([], form) ?? {}) as Record<string, unknown>;

  return (
    <>
      {sections.map((section, idx) => {
        const visible = section.visibleWhen ? section.visibleWhen(values as never) : true;
        const title = section.titleKey ? <SectionTitle k={section.titleKey} /> : undefined;
        return (
          <FormSection
            key={section.titleKey ?? `section-${idx}`}
            title={title}
            visible={visible}
          >
            <FieldsRenderer<T>
              entries={section.fields}
              schema={schema as Record<string, never>}
            />
          </FormSection>
        );
      })}
    </>
  );
}

function SectionTitle({ k }: { k: string }) {
  const { t } = useTranslation();
  return <>{t(k)}</>;
}

// ─── Fields renderer — string | array | object | custom ─────────────────

function FieldsRenderer<T>({
  entries,
  schema,
}: {
  entries: FieldEntry<T>[];
  schema: Record<string, never>;
}) {
  return (
    <>
      {entries.map((entry, idx) => (
        <FieldEntryRenderer<T> key={idx} entry={entry} schema={schema} />
      ))}
    </>
  );
}

function FieldEntryRenderer<T>({
  entry,
  schema,
}: {
  entry: FieldEntry<T>;
  schema: Record<string, never>;
}): ReactNode {
  // String key → full-width field
  if (typeof entry === "string") {
    const field = schema[entry];
    if (!field) {
      Sentry.captureMessage(`[SchemaForm] unknown field key: "${entry}"`, "warning");
      return null;
    }
    return <SchemaFormItem<T> field={field} />;
  }

  // Custom escape hatch
  if (!Array.isArray(entry) && typeof entry === "object" && "custom" in entry) {
    return <>{(entry as { custom: ReactNode }).custom}</>;
  }

  // Inline field object (e.g. a `pickRef` relation column carrying its own
  // `edit`) — render it directly, no schema key lookup.
  if (!Array.isArray(entry) && typeof entry === "object" && "field" in entry) {
    return <SchemaFormItem<T> field={(entry as { field: never }).field} />;
  }

  // Object form — schema key with per-call overrides
  if (!Array.isArray(entry) && typeof entry === "object" && "key" in entry) {
    const obj = entry;
    const field = schema[obj.key];
    if (!field) {
      Sentry.captureMessage(`[SchemaForm] unknown field key: "${obj.key}"`, "warning");
      return null;
    }
    return (
      <SchemaFormItem<T>
        field={field}
        widget={obj.widget}
        placeholder={obj.placeholder}
        helpText={obj.helpText}
        autoFocus={obj.autoFocus}
        required={obj.required}
      />
    );
  }

  // Nested array → grid row with N columns (one per entry)
  if (Array.isArray(entry)) {
    const columns = entry.length;
    return (
      <Grid columns={columns} gap="md">
        {entry.map((sub, idx) => (
          <FieldEntryRenderer<T> key={idx} entry={sub} schema={schema} />
        ))}
      </Grid>
    );
  }

  return null;
}
